// JOBE-0044.13: WhyThisRow — the "Why this row · filter trace" inspector
// mode content (umbrella §R8 mode-swap, design-handoff §Direction B).
//
// Renders a per-chip bullet list confirming each active filter
// predicate against the currently-selected EntityRow:
//
//   matched · type      → document ✓
//   matched · owner     → Alice ✓
//   matched · since     → last changed 2026-05-15… ✓
//
// Plus the wire-side query_entities fragment for the selected row (the
// per-row view of the same data the TranslatedCaption surfaces above
// the table). Both surfaces are entirely client-side; no new fetch.
//
// Empty-state (no active predicate chips, scope-only) renders the brief
// explanation pinned by the .13 follow-up spec: "Every entity you can
// read. Add filters above to see why this row was included."

import { whyThisRow } from './whyThisRow.js';

function statusGlyph(status) {
  if (status === 'matched') return '✓';
  if (status === 'unmatched') return '✗';
  return '·';
}

function wireFragment(chip) {
  // Mirrors TranslatedCaption's formatting so the per-row trace and the
  // per-page trust receipt read identically.
  const wire = chip.wirePredicate;
  const values = (chip.values || []).join(', ');
  if (wire === 'committedAt>=') return `committedAt ≥ ${values}`;
  if (wire === 'committedAt<') return `committedAt < ${values}`;
  return `${wire} ∈ {${values}}`;
}

export function WhyThisRow({ chips, entity }) {
  const predicateChips = (chips || []).filter((c) => c && c.kind === 'predicate');

  if (predicateChips.length === 0) {
    return (
      <section className="library-inspector-why">
        <div className="library-inspector-preview-label">
          Why this row · filter trace
        </div>
        <p className="library-inspector-why-empty">
          Every entity you can read. Add filters above to see why this row
          was included.
        </p>
      </section>
    );
  }

  const trace = whyThisRow(predicateChips, entity);

  return (
    <section className="library-inspector-why">
      <div className="library-inspector-preview-label">
        Why this row · filter trace
      </div>

      <ul className="library-inspector-why-list">
        {trace.map((t, i) => (
          <li
            key={`${t.friendlyToken}-${i}`}
            className={`library-inspector-why-row library-inspector-why-${t.status}`}
            data-status={t.status}
          >
            <span className="library-inspector-why-label">
              matched · {t.friendlyToken}
            </span>
            <span className="library-inspector-why-detail">
              {t.detail}
              {' '}
              <span className="library-inspector-why-glyph" aria-hidden="true">
                {statusGlyph(t.status)}
              </span>
            </span>
          </li>
        ))}
      </ul>

      <div className="library-inspector-why-wire">
        <div className="library-inspector-preview-label">
          Translated to query_entities
        </div>
        <div className="library-inspector-why-wire-body">
          {predicateChips.map((c, i) => (
            <span key={i} className="library-inspector-why-wire-fragment">
              {i > 0 ? ' · ' : null}
              {wireFragment(c)}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
